import { HTMLAttributes, useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/common/Button/Button';
import { Icon, icons } from '@/components/common/Icon';
import Image from '@/components/common/Image';

interface CareerImage {
  src: string;
  alt: string;
}

interface CareersProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  buttonText: string;
  buttonHref: string;
  images: CareerImage[];
  tag?: string;
}

export const Careers = ({
  title,
  description,
  buttonText,
  buttonHref,
  images,
  tag,
  className,
  ...props
}: CareersProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((current) => (current + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  const nextSlide = () => {
    setCurrentIndex((current) => (current + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex((current) => (current - 1 + images.length) % images.length);
  };

  return (
    <section className={cn('py-20 bg-black text-white', className)} {...props}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            {tag && <span className="text-blue-500 text-base font-semibold mb-3 lg:-mt-16 block">{tag}</span>}
            <h2 className="text-4xl font-bold mb-6">{title}</h2>
            <p className="text-xl text-gray-300 mb-8">{description}</p>
            <Button
              variant="solid"
              color="primary"
              onClick={() => window.open("https://wellfound.com/company/cobalt-8", "_blank")}
            >
              {buttonText}
            </Button>
          </div>

          <div className="relative h-[500px] rounded-2xl overflow-hidden">
            {images.map((image, index) => (
              <div
                key={index}
                className={cn(
                  'absolute inset-0 transition-opacity duration-500',
                  index === currentIndex ? 'opacity-100' : 'opacity-0'
                )}
              >
                <Image
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent" />
              </div>
            ))}

            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              aria-label="Previous slide"
            >
              <Icon icon={icons.chevronLeft} className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              aria-label="Next slide"
            >
              <Icon icon={icons.chevronRight} className="w-6 h-6 text-white" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={cn(
                    'w-2 h-2 rounded-full transition-all',
                    index === currentIndex ? 'bg-white w-4' : 'bg-white/50'
                  )}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};